import { useEffect } from 'react';
import Breadcrumbs from '@v2/shared/components/Breadcrumbs';
import ContentCard from '@v2/shared/components/ContentCard';
import { trackEvent } from '@v2/shared/lib/analytics';
import useUrlGenerator from '@v2/shared/lib/useUrlGenerator';
import Band from './Band';
import DoItYourself from './DoItYourself';
import Feedback from './Feedback';
import LinkList from './LinkList';
import SectionCard from './SectionCard';
import StillNeedHelp from './StillNeedHelp';
import { recordView } from './kustomer';
import { CARD_GAP, H2 } from './styles';
import { HelpCenterArticleProps } from './types';
import { withLiveAgentHours } from '@v2/shared/lib/supportHours';

export default function HelpArticle({ props }: { props: HelpCenterArticleProps }) {
  const { buildUrl } = useUrlGenerator();
  const { article, category } = props;

  useEffect(() => {
    trackEvent('help_article_view', { slug: article.slug, category: category ? category.slug : '' });
    recordView(props.kustomer, article.id);
  }, [article.slug, article.id, category, props.kustomer]);

  return (
    <>
      <Breadcrumbs items={props.breadcrumbs} />

      <Band
        title={article.title}
        intro={article.summary}
        meta={article.updated ? `Updated ${article.updated}` : undefined}
        searchAction={props.searchAction}
        kustomer={props.kustomer}
        categoryTitles={props.categoryTitles}
      />

      <ContentCard className={CARD_GAP}>
        {props.headings.length > 0 ? (
          <nav
            aria-label="On this page"
            className="mb-5 flex flex-wrap items-baseline gap-x-3.5 gap-y-1 border-0 border-b border-solid border-slate-200 pb-3.5 text-[13px]"
          >
            <b className="mr-1 text-xs font-bold uppercase tracking-wider text-slate-500">On this page</b>
            {props.headings.map((heading) => (
              <a
                key={heading.id}
                href={`#${heading.id}`}
                className="font-semibold text-primary no-underline hover:underline"
              >
                {heading.text}
              </a>
            ))}
          </nav>
        ) : null}

        {/*
         * The body is HTML authored in Kustomer, carrying the inline styles its design
         * lives in. Sanitized server-side against an allowlist before it was cached --
         * see HtmlSanitizer -- which is what makes this safe to set.
         *
         * `help-article-body` is not a utility: the two responsive table rules it needs
         * are attribute selectors on markup no component wrote, so they live in
         * styles/v2.css beside .product-description, which exists for exactly the same
         * reason.
         */}
        <div
          className="help-article-body"
          dangerouslySetInnerHTML={{ __html: withLiveAgentHours(props.body) }}
        />
      </ContentCard>

      <SectionCard>
        <Feedback kustomer={props.kustomer} articleId={article.id} slug={article.slug} />
      </SectionCard>

      <SectionCard>
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          {category && props.moreInCategory.length > 0 ? (
            <div>
              <h2 className={H2}>More in {category.title}</h2>
              <LinkList links={props.moreInCategory} />
              <p className="mb-0 mt-3">
                <a
                  href={buildUrl(category.href)}
                  className="font-semibold text-primary no-underline hover:underline"
                >
                  All {category.title} articles &rarr;
                </a>
              </p>
            </div>
          ) : null}

          <DoItYourself />
        </div>
      </SectionCard>

      <StillNeedHelp />
    </>
  );
}
